import { useId, useState, type RefObject } from "react";
import dayjs from "dayjs";
import { Tooltip, useFloatingPortal } from "@foris/avocado-suite";
import WeekTooltipLabel from "@common/components/week-tooltip-label";
import type { AnnualScheduleWeek } from "../../types";
import styles from "./annualScheduleCell.module.scss";

interface Props {
  count: number;
  week: AnnualScheduleWeek;
  label: string;
  onSelect: () => void;
}

/** A populated cell with the weekly calendar tooltip, rendered outside the scroll viewport. */
export default function AnnualScheduleCell({ count, week, label, onSelect }: Props) {
  const tooltipId = useId();
  const [hovered, setHovered] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isOpen = hovered && !dismissed;
  const { referenceRef, Portal } = useFloatingPortal({ isOpen, offset: [0, -14] });

  return (
    <>
      <button
        ref={referenceRef as RefObject<HTMLButtonElement>}
        type="button"
        className={styles.annualScheduleCell}
        aria-label={label}
        aria-describedby={isOpen ? tooltipId : undefined}
        onMouseEnter={() => {
          setHovered(true);
          setDismissed(false);
        }}
        onMouseLeave={() => setHovered(false)}
        onKeyDown={event => {
          if (event.key === "Escape") setDismissed(true);
        }}
        onClick={() => {
          setDismissed(true);
          onSelect();
        }}
      >
        {count}
      </button>
      <Portal>
        <Tooltip
          visible
          placement="top"
          label={
            <div id={tooltipId} role="tooltip">
              <WeekTooltipLabel
                weekLabel={`S${week.catalogNumber}`}
                startingDate={dayjs(week.startingDate).format("DD.MM")}
                endingDate={dayjs(week.endingDate).format("DD.MM")}
                endingYear={dayjs(week.endingDate).format("YYYY")}
              />
            </div>
          }
        >
          <span className={styles.annualScheduleCell_tooltipAnchor} />
        </Tooltip>
      </Portal>
    </>
  );
}
